import { Component, inject } from '@angular/core';
import { SettingService } from '../../../services/setting.service';
import { FNFResponse } from '../../../models/response';
import { ToastrService } from 'ngx-toastr';
import { NgxSpinnerService } from 'ngx-spinner';
import { CommonModule } from '@angular/common';
import { RestaurantService } from '../../../services/restaurant.service';
import { Router } from '@angular/router';
import { forkJoin } from 'rxjs';
import { isPosManaged } from '../../../utils/pos-provider.util';

interface PaymentMethod {
  /** `payment_type` sent to the settings API. */
  key: string;
  title: string;
  description: string;
  icon: string;
}

@Component({
  selector: 'app-payment-setting',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './payment-setting.component.html',
  styleUrl: './payment-setting.component.css',
})
export class PaymentSettingComponent {
  restaurantService = inject(RestaurantService);
  router = inject(Router);
  settingService = inject(SettingService);
  toastr = inject(ToastrService);
  ngxSpinnerService = inject(NgxSpinnerService);

  currentRestaurantSettings: any;
  displayLoading: boolean = false;
  restaurantPosProvider: string = 'fnf';

  readonly methods: PaymentMethod[] = [
    {
      key: 'cash',
      title: 'Cash Payment',
      description: 'Accept cash payments at the counter',
      icon: 'bx bx-money',
    },
    {
      key: 'terminal_credit_card',
      title: 'Terminal Payment',
      description: 'Accept card payments via POS terminal',
      icon: 'bx bx-credit-card',
    },
    {
      key: 'online_payment',
      title: 'Online Payment',
      description: 'Accept payments through online ordering',
      icon: 'bx bx-globe',
    },
    {
      key: 'google_pay',
      title: 'Google Pay',
      description: 'Accept Google Pay contactless payments',
      icon: 'bx bx-mobile-alt',
    },
    {
      key: 'apple_pay',
      title: 'Apple Pay',
      description: 'Accept Apple Pay contactless payments',
      icon: 'bx bx-mobile-alt',
    },
  ];

  /** Values as last loaded from (or written to) the server. */
  private saved: Record<string, boolean> = {};
  /** Unsaved edits shown in the UI; committed on Save Changes. */
  draft: Record<string, boolean> = {};

  constructor() {
    this.restaurantService.restaurantPosProvider$.subscribe((posProvider: any) => {
      this.restaurantPosProvider = posProvider;
      if (isPosManaged(this.restaurantPosProvider)) {
        this.router.navigate(['/']);
      }
    });
  }

  ngOnInit() {
    this.displayLoading = true;
    this.settingService.currentRestaurantSettings$.subscribe((settings: any) => {
      this.currentRestaurantSettings = settings;
      const config = settings?.find(
        (type: any) => type.setting_type === 'payment',
      )?.settings_config;

      this.saved = {};
      this.methods.forEach((method) => {
        this.saved[method.key] = config?.[method.key]?.enabled || false;
      });
      this.draft = { ...this.saved };
    });
    this.displayLoading = false;
  }

  toggle(key: string, event: any) {
    // Local only — nothing reaches the server until Save Changes.
    this.draft[key] = event.target.checked;
  }

  get changedKeys(): string[] {
    return this.methods
      .map((method) => method.key)
      .filter((key) => this.draft[key] !== this.saved[key]);
  }

  get isDirty(): boolean {
    return this.changedKeys.length > 0;
  }

  cancel() {
    this.draft = { ...this.saved };
  }

  save() {
    const changed = this.changedKeys;
    if (!changed.length) {
      return;
    }

    this.displayLoading = true;
    this.ngxSpinnerService.show();

    forkJoin(
      changed.map((key) =>
        this.settingService.changePaymentSettings({
          payment_type: key,
          value: this.draft[key],
        }),
      ),
    ).subscribe({
      next: (responses: FNFResponse<any>[]) => {
        this.displayLoading = false;
        this.ngxSpinnerService.hide();

        const failed = responses.find((response) => !response.data);
        if (failed) {
          this.toastr.error(
            failed.error?.error?.message || failed.error?.message || 'No data found!',
            'Error',
          );
          return;
        }

        // The last response carries the fully updated config.
        const updatedConfig = responses[responses.length - 1].data.settings_config;
        this.currentRestaurantSettings?.forEach((type: any) => {
          if (type.setting_type === 'payment') {
            type.settings_config = updatedConfig;
          }
        });
        this.settingService.currentRestaurantSettings$.next(this.currentRestaurantSettings);
        this.toastr.success('Payment settings updated successfully', 'Success');
      },
      error: () => {
        this.displayLoading = false;
        this.ngxSpinnerService.hide();
        this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
      },
    });
  }
}
